import { useEffect, useId, useLayoutEffect, useState, useRef } from "react";
import "../css/DropdownKey.css";
import { inputOnEnter } from "../js/utils";
import TooltipBox from "./Tooltip";

/**
 * Igual que Dropdown, pero sin desplegar muestra la clave (id) de la opción
 * seleccionada en vez de su texto. Se puede escribir tanto la clave como el
 * texto para buscar/seleccionar una opción.
 *
 * format: "default" (por defecto) o "table-cell" (mismo aspecto que los
 * inputs de las celdas de tabla: esquinas cuadradas y mismo tamaño de letra).
 *
 * includeKeyInOption: si es true, las opciones desplegadas se muestran como
 * "clave - texto" en vez de solo el texto.
 *
 * tooltip: si es true, al pasar el ratón muestra el texto de la opción
 * seleccionada, también cuando el componente está desactivado.
 */
export default function DropdownKey({
  id,
  icon,
  size,
  disabled,
  mandatory = true,
  optionList = [],
  op = null,
  tabIndex,
  format = "default",
  includeKeyInOption = false,
  tooltip = false,
  onChange,
}) {
  const [togglePanel, setTogglePanel] = useState(false);
  const [inputSearch, setInputSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredList, setFilteredList] = useState(optionList);
  const [alignRight, setAlignRight] = useState(false);

  const dropdownRef = useRef(null);
  const mouseInside = useRef(false);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const tooltipId = "ddk-tt-" + useId();

  const getComboboxSize = () => {
    const sizes = [
      "combobox-key-full",
      "combobox-key-small",
      "combobox-key-medium",
      "combobox-key-large",
      "combobox-key-super-small",
    ];
    return sizes[size] || "combobox-key-default";
  };

  const getFormat = () =>
    format === "table-cell" ? "combobox-key-table-cell" : "";

  const stripAccents = (text) =>
    String(text ?? "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  // Clave de una opción, tal y como se muestra en el input.
  const keyOf = (item) => String(item?.id ?? item?.value ?? "").toUpperCase();

  // Texto de una opción en el panel desplegado.
  const optionLabel = (item) => {
    const key = keyOf(item);
    return includeKeyInOption && key !== ""
      ? `${key} - ${item.text}`
      : item.text;
  };

  const indexOfKey = (list, key) =>
    list.findIndex((item) => keyOf(item) === key);

  const tooltipText =
    tooltip && !togglePanel && inputSearch !== ""
      ? (optionList[indexOfKey(optionList, inputSearch)]?.text ?? "")
      : "";

  const scrollToSelected = () => {
    const items = dropdownRef.current?.querySelectorAll(".dropdown-op") || [];
    const selected = items[selectedIndex];
    selected?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const chooseOption = (item, returnFocus = true) => {
    if (!item) return;
    setInputSearch(keyOf(item));
    setTogglePanel(false);
    onChange(item);
    if (returnFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const applyFilter = (text) => {
    const upperText = stripAccents(text.toUpperCase());
    const filtered = optionList.filter(
      (item) =>
        stripAccents(keyOf(item)).includes(upperText) ||
        stripAccents(item.text).toUpperCase().includes(upperText),
    );
    setFilteredList(filtered);
    const currentIndex = indexOfKey(filtered, inputSearch);
    setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);
    setTogglePanel(true);
  };

  const handleKey = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!filteredList.length) return;
        setSelectedIndex((i) => (i + 1) % filteredList.length);
        break;

      case "ArrowUp":
        e.preventDefault();
        if (!filteredList.length) return;
        setSelectedIndex(
          (i) => (i - 1 + filteredList.length) % filteredList.length,
        );
        break;

      case "Enter":
        e.preventDefault();
        if (togglePanel && filteredList.length) {
          chooseOption(filteredList[selectedIndex]);
        } else {
          inputOnEnter(e);
        }
        break;

      case "Escape":
        setTogglePanel(false);
        break;

      case "Tab":
        if (mandatory) {
          chooseOption(filteredList[selectedIndex] || null, false);
        } else {
          onChange({ id: inputSearch, text: inputSearch });
        }
        inputOnEnter(e);
        break;

      default:
        if (!mandatory) {
          onChange({ id: inputSearch, text: inputSearch });
        }
    }
  };

  const handleUnfocus = () => {
    if (!mandatory) {
      onChange({ id: inputSearch, text: inputSearch });
      return;
    }

    // Coincidencia exacta por clave o por texto
    const exactMatch = optionList.find(
      (item) =>
        keyOf(item) === inputSearch ||
        String(item.text ?? "").toUpperCase() === inputSearch,
    );
    if (exactMatch) {
      chooseOption(exactMatch, false);
      return;
    }

    let text = stripAccents(inputSearch);
    let match = null;
    while (text.length > 0 && !match) {
      match = optionList.find(
        (item) =>
          stripAccents(keyOf(item)).startsWith(text) ||
          stripAccents(item.text).toUpperCase().startsWith(text),
      );
      if (!match) text = text.slice(0, -1);
    }

    chooseOption(match || optionList[0] || null, false);
  };

  // Effects
  useEffect(() => {
    setFilteredList(optionList);
  }, [optionList]);

  useEffect(() => {
    if (op == null) return;
    const selected = mandatory
      ? optionList.find((item) => item.id === op || item.value === op)
      : { id: op, text: op };
    setInputSearch(selected ? keyOf(selected) : "");
  }, [op, optionList]);

  useEffect(scrollToSelected, [selectedIndex]);

  // Si el panel de opciones no cabe a la derecha de la pantalla, se alinea
  // con el borde derecho del desplegable para que se abra hacia la izquierda.
  useLayoutEffect(() => {
    if (!togglePanel || !optionsRef.current || !dropdownRef.current) return;
    const { left, right } = dropdownRef.current.getBoundingClientRect();
    const panelWidth = optionsRef.current.offsetWidth;
    const viewportWidth = document.documentElement.clientWidth;
    setAlignRight(left + panelWidth > viewportWidth && right - panelWidth >= 0);
  }, [togglePanel, filteredList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTogglePanel(false);
      }
    };

    if (togglePanel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [togglePanel]);

  return (
    <div
      id={id}
      className={`combobox-key ${getComboboxSize()} ${getFormat()}`}
      ref={dropdownRef}
      onBlur={() => !mouseInside.current && handleUnfocus()}
      onMouseEnter={() => (mouseInside.current = true)}
      onMouseLeave={() => (mouseInside.current = false)}
    >
      <div
        data-tooltip-id={tooltipText !== "" ? tooltipId : undefined}
        className={`dropdown pure-dropdown ${
          togglePanel ? "dropdown-open" : ""
        } ${disabled ? "dropdown-disabled" : ""}`}
        onClick={() => {
          if (!disabled) {
            setFilteredList(optionList);

            const currentIndex = indexOfKey(optionList, inputSearch);
            setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);

            setTogglePanel((open) => !open);
          }
        }}
      >
        {icon && <img src={icon} alt="icon" />}
        <div className="languagetext">
          <input
            ref={inputRef}
            className="text"
            tabIndex={tabIndex}
            value={inputSearch}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              setInputSearch(value);
              applyFilter(value);
            }}
            onKeyUp={handleKey}
            onKeyDown={(e) => e.key === "Tab" && handleKey(e)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
        <div className="downicon">
          <img
            src="/images/blue-down-arrow.svg"
            loading="lazy"
            alt="Panel icon"
            className="vectors-wrapper-11"
          />
        </div>
      </div>

      {tooltipText !== "" && (
        <TooltipBox id={tooltipId} content={tooltipText} />
      )}

      {togglePanel && (
        <div
          ref={optionsRef}
          className={`dropdown-options ${
            alignRight ? "dropdown-options-right" : ""
          }`}
        >
          {filteredList.length === 0 ? (
            <></>
          ) : (
            filteredList.map((item, i) => (
              <div
                key={i}
                className={`dropdown-op ${
                  i === selectedIndex ? "selected-op" : ""
                }`}
                onClick={(e) => {
                  chooseOption(item);
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  chooseOption(item);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <span>{optionLabel(item)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
